import {Timestamp} from '@mintdata-wsp/common/state/ui/visualDesigner/DesignVersionsState';
import {MeshLocation} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshLocation';
import {IdUtil} from '@mintdata/mint-kit/impl/util/IDUtil';
import {action, observable} from 'mobx';
import {createTransformer, ITransformer} from 'mobx-utils';
import {DesignStateFromJsonCtx} from '@mintdata-wsp/common/state/ui/visualDesigner/DesignState';

export type MeshCellNoteId = string;

export enum MeshCellNoteProps
{
    Title = 'title',
    Text = 'text',

    CreatedBy = 'createdBy',
    CreatedAt = 'createdAt',
    UpdatedBy = 'updatedBy',
    UpdatedAt = 'updatedAt',

    Width = 'width',
    Height = 'height',
}

export type ISheetCellNote = any;

export interface IMeshCellNote extends ISheetCellNote
{
    [MeshCellNoteProps.Title]:string;
    [MeshCellNoteProps.Text]:string;

    [MeshCellNoteProps.CreatedBy]:string;
    [MeshCellNoteProps.CreatedAt]:Timestamp;
    [MeshCellNoteProps.UpdatedBy]:string;
    [MeshCellNoteProps.UpdatedAt]:Timestamp;

    [MeshCellNoteProps.Width]:number;
    [MeshCellNoteProps.Height]:number;
}

export interface IMeshCellNoteSnapshot extends IMeshCellNote
{
    id:MeshCellNoteId;
}

export enum CellNotesSortOrder
{
    ASC = 1,
    DESC = -1
}

export interface IOrderedMeshCellNote
{
    location:MeshLocation;
    cellNote:MeshCellNote;
}

export class MeshCellNote implements IMeshCellNote
{
    public id:MeshCellNoteId;

    @observable
    public title:string;

    @observable
    public text:string;

    @observable
    public createdBy:string;

    @observable
    public createdAt:Timestamp;

    @observable
    public updatedBy:string;

    @observable
    public updatedAt:Timestamp;

    @observable
    public width:number;

    @observable
    public height:number;

    private constructor()
    {
    }

    public static create():MeshCellNote
    {
        const meshCellNote:MeshCellNote = new MeshCellNote();

        meshCellNote.id = IdUtil.newGUID();
        meshCellNote.title = '';
        meshCellNote.text = 'Note';
        meshCellNote.width = 150;
        meshCellNote.height = 55;

        return meshCellNote;
    }

    public get colIndex()
    {
        return MeshLocation.fromKey(this.id).colIndex;
    }

    public get rowIndex()
    {
        return MeshLocation.fromKey(this.id).rowIndex;
    }

    public getProperty<T extends keyof IMeshCellNote>(prop:T):IMeshCellNote[T]
    {
        return (this as any)[prop];
    }

    public clone():MeshCellNote
    {
        const meshCellNote:MeshCellNote = MeshCellNote.create();

        meshCellNote.title = this.title;
        meshCellNote.text = this.text;
        meshCellNote.createdBy = this.createdBy;
        meshCellNote.createdAt = this.createdAt;
        meshCellNote.updatedBy = this.updatedBy;
        meshCellNote.updatedAt = this.updatedAt;
        meshCellNote.width = this.width;
        meshCellNote.height = this.height;

        return meshCellNote;
    }

    @action
    public setProperty<T extends keyof IMeshCellNote>(prop:T, value:IMeshCellNote[T])
    {
        (this as any)[prop] = value;
    }

    @action
    public setNote(note:MeshCellNote)
    {
        this.title = note.title;
        this.text = note.text;
        this.createdBy = note.createdBy;
        this.createdAt = note.createdAt;
        this.updatedBy = note.updatedBy;
        this.updatedAt = note.updatedAt;
        this.width = note.width;
        this.height = note.height;
    }

    public static toJson:ITransformer<MeshCellNote, IMeshCellNoteSnapshot> = createTransformer((meshCellNote:MeshCellNote) =>
    {
        return {
            id: meshCellNote.id,
            title: meshCellNote.title,
            text: meshCellNote.text,
            createdBy: meshCellNote.createdBy,
            createdAt: meshCellNote.createdAt,
            updatedBy: meshCellNote.updatedBy,
            updatedAt: meshCellNote.updatedAt,
            width: meshCellNote.width,
            height: meshCellNote.height
        };
    });

    public static fromJson(json:IMeshCellNoteSnapshot,
                           currentState?:MeshCellNote,
                           currentJson?:IMeshCellNoteSnapshot,
                           ctx?:DesignStateFromJsonCtx):MeshCellNote
    {
        return MeshCellNote.fromJsonDeep(json);
    }

    public static fromJsonDeep(json:IMeshCellNoteSnapshot):MeshCellNote
    {
        const meshCellNote:MeshCellNote = new MeshCellNote();

        meshCellNote.id = json.id;
        meshCellNote.title = json.title;
        meshCellNote.text = json.text;
        meshCellNote.createdBy = json.createdBy;
        meshCellNote.createdAt = json.createdAt;
        meshCellNote.updatedBy = json.updatedBy;
        meshCellNote.updatedAt = json.updatedAt;
        meshCellNote.width = json.width;
        meshCellNote.height = json.height;

        return meshCellNote;
    }
}
